/**
 * Card header (#2244) — author name, ownership label, resolved chip,
 * and the timestamp. Split from `comment-card.tsx` so no single
 * comment file needs `eslint-disable max-lines`.
 */

import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { formatDateAgo } from '@utils/date.ts';

export interface CommentCardHeaderProps {
  authorName: string | undefined;
  createdAt: string | undefined;
  /** Optional pill (e.g. `Source` / `Translation`). */
  ownershipLabel?: string;
  /** Show the green `Resolved` chip. */
  resolved?: boolean;
}

export function CommentCardHeader({
  authorName,
  createdAt,
  ownershipLabel,
  resolved
}: CommentCardHeaderProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  // Stack author/time on row 1 and the chips on row 2 so a narrow
  // (220-240px) gutter card doesn't fight to pack `Translation` +
  // `Resolved` + timestamp into one line and overlap them. The chips
  // row is only rendered when a chip exists, so single-pane cards
  // stay single-row.
  return (
    <header className="min-w-0">
      <div className="flex items-center justify-between gap-2 min-w-0">
        <p
          className="text-xs font-medium truncate m-0"
          style={{ color: getColour('normalText') }}
        >
          {authorName ?? t('unknown')}
        </p>
        <time
          dateTime={createdAt}
          className="text-[11px] shrink-0"
          style={{ color: getColour('inactiveText') }}
        >
          {createdAt ? formatDateAgo(createdAt) : ''}
        </time>
      </div>
      {(ownershipLabel || resolved) && (
        <div className="mt-1 flex items-center gap-1 flex-wrap">
          {ownershipLabel && (
            <span
              className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border shrink-0"
              style={{
                color: getColour('inactiveText'),
                borderColor: getColour('border'),
                backgroundColor: getColour('firstLayer')
              }}
            >
              {ownershipLabel}
            </span>
          )}
          {resolved && (
            <span
              className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full shrink-0"
              style={{
                color: getColour('success'),
                backgroundColor: `${getColour('success')}22`
              }}
            >
              <CheckCircle2 size={10} aria-hidden="true" />
              {t('resolved')}
            </span>
          )}
        </div>
      )}
    </header>
  );
}
